import type {
  Assignment,
  MapDefinition,
  Slot,
  Tactic,
  Utility,
} from "@cs2/contracts/domain";
import { utilitiesForStep } from "../../../shared/lib/tactic-selectors.js";
import { UtilityCard } from "../../../shared/utility/UtilityCard.js";
import { UtilityStepChip } from "../../../shared/utility/UtilityStepChip.js";

/**
 * Rechte Spalte der Quickview: Schrittliste mit Wiedergabesteuerung, der
 * Fallback-Text und die Utility-Karten des gewählten Spielers.
 *
 * Rein darstellend — bis Phase 11 waren das 113 Zeilen JSX in
 * `QuickviewPage.tsx`.
 */
export function QuickviewDetail({
  tactic,
  map,
  assignment,
  player,
  assignedUtilities,
  selectedStepOrder,
  setSelectedStepOrder,
  setOpenUtility,
}: {
  tactic: Tactic;
  map: MapDefinition;
  assignment: Assignment;
  player: Slot;
  assignedUtilities: Utility[];
  selectedStepOrder: number | null;
  setSelectedStepOrder: (
    order: number | null | ((current: number | null) => number | null),
  ) => void;
  setOpenUtility: (utility: Utility | null) => void;
}) {
  return (
    <aside className="qv-detail">
      <header>
        <p>MEINE AUFGABE</p>
        <h1>
          {player} · {assignment.role}
        </h1>
        <strong>{assignment.summary}</strong>
        <div>
          <span>{tactic.buyType}</span>
          <span>{assignment.timing}</span>
        </div>
      </header>
      <section className="qv-steps">
        <h2>SCHRITT FÜR SCHRITT</h2>
        <ol className="qv-frame-list">
          {assignment.steps.map((step, index) => {
            const stepUtilities = utilitiesForStep(
              assignedUtilities,
              assignment,
              step.order,
            );
            return (
              <li
                key={step.order}
                className={step.order === selectedStepOrder ? "active" : ""}
              >
                <button
                  type="button"
                  className="qv-step-head"
                  aria-pressed={step.order === selectedStepOrder}
                  onClick={() =>
                    setSelectedStepOrder((current) =>
                      current === step.order ? null : step.order,
                    )
                  }
                >
                  <b>{index + 1}</b>
                  <span>
                    <strong>{step.title}</strong>
                    <small>{step.detail}</small>
                  </span>
                </button>
                {stepUtilities.length ? (
                  <ul className="qv-step-utilities">
                    {stepUtilities.map((utility) => (
                      <UtilityStepChip
                        key={utility.id}
                        utility={utility}
                        offsetSeconds={step.offsetSeconds}
                        colorize
                        onSelect={() => setOpenUtility(utility)}
                      />
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ol>
      </section>
      <section className="qv-fallback">
        <h2>FALLBACK</h2>
        <p>{tactic.fallback}</p>
      </section>
      <section className="qv-utility">
        <div>
          <h2>UTILITY PLAN – {player}</h2>
          <span>
            {assignedUtilities.every(
              (item) => item.verificationState === "verified",
            )
              ? "VERIFIZIERT"
              : "CAPTURE OFFEN"}
          </span>
        </div>
        <ol className="qv-frame-list">
          {assignedUtilities.map((utility) => (
            <li key={utility.id}>
              <UtilityCard
                utility={utility}
                onOpen={() => setOpenUtility(utility)}
                fallbackRadarPath={map.radarPath}
                variant="quickview"
              />
            </li>
          ))}
        </ol>
      </section>
    </aside>
  );
}
